import { useEffect, useRef, useState } from 'react';

/**
 * Animate a number from 0 → target when the ref enters the viewport.
 * Returns: { ref, value }
 *
 * @param {number} target  — final number
 * @param {object} opts    — { duration?, decimals?, suffix?, prefix?, once? }
 */
export function useCountUp(target, opts = {}) {
    const { duration = 1400, decimals = 0, once = true } = opts;
    const ref = useRef(null);
    const [value, setValue] = useState(0);
    const startedRef = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    if (once && startedRef.current) return;
                    startedRef.current = true;
                    const start = performance.now();
                    const tick = (now) => {
                        const t = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - t, 3);
                        const next = Number((eased * target).toFixed(decimals));
                        setValue(next);
                        if (t < 1) requestAnimationFrame(tick);
                        else setValue(target);
                    };
                    requestAnimationFrame(tick);
                    if (once) observer.unobserve(el);
                });
            },
            { threshold: 0.4 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [target, duration, decimals, once]);

    return { ref, value };
}
